import React, { useEffect, useState } from "react";
import { Phone, Delete } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface DialerKeypadProps {
  value: string;
  onChange: (value: string) => void;
  onCall: () => void;
  disabled?: boolean;
  inCallMode?: boolean;
}

const DialerKeypad: React.FC<DialerKeypadProps> = ({
  value,
  onChange,
  onCall,
  disabled = false,
  inCallMode,
}) => {
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isLongPress, setIsLongPress] = useState(false);

  const keys = [
    { digit: "1", letters: "" },
    { digit: "2", letters: "ABC" },
    { digit: "3", letters: "DEF" },
    { digit: "4", letters: "GHI" },
    { digit: "5", letters: "JKL" },
    { digit: "6", letters: "MNO" },
    { digit: "7", letters: "PQRS" },
    { digit: "8", letters: "TUV" },
    { digit: "9", letters: "WXYZ" },
    { digit: "*", letters: "" },
    { digit: "0", letters: "+" },
    { digit: "#", letters: "" },
  ];

  const handleKeyPress = (key: string) => {
    onChange(value + key);
    playDialTone();

    if (inCallMode) {
      console.log("Sending DTMF tone:", key);
      // TODO: Send DTMF tone through your call API
    }
  };

  const handleBackspace = () => {
    onChange(value.slice(0, -1));
  };

  const handleLongPressZero = () => {
    if (value.endsWith("0")) {
      onChange(value.slice(0, -1) + "+");
    } else {
      onChange(value + "+");
    }
    playDialTone();
  };

  const playDialTone = () => {
    const dialSoundVersion = Math.floor(Math.random() * 10) + 1;
    const audio = new Audio(`/audio/dial/dial_${dialSoundVersion}.m4a`);
    audio.play().catch(() => {
      //TODO handle missing sounds.
      // Ignore audio play errors
    });
  };

  useEffect(() => {
    return () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
      }
    };
  }, [longPressTimer]);

  return (
    <div className="flex flex-col items-center space-y-4 w-full max-w-sm mx-auto">
      {/* Display Input */}
      <div className="w-full">
        <Input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter phone number"
          className="text-2xl text-center h-14 font-semibold tracking-wider dark:text-white"
        />
      </div>

      <div className="grid grid-cols-3 gap-3 w-full">
        {keys.map((key) => (
          <Button
            key={key.digit}
            variant="outline"
            className={cn(
              "h-16 flex flex-col items-center justify-center",
              "hover:bg-accent transition-colors",
              "dark:border-gray-700",
            )}
            onClick={() => !isLongPress && handleKeyPress(key.digit)}
            onMouseDown={(e) => {
              if (key.digit === "0") {
                const timer = setTimeout(() => {
                  setIsLongPress(true);
                  handleLongPressZero();
                }, 500);
                setLongPressTimer(timer);
              }
            }}
            onMouseUp={() => {
              if (longPressTimer) {
                clearTimeout(longPressTimer);
                setLongPressTimer(null);
              }
              setTimeout(() => setIsLongPress(false), 100);
            }}
            onMouseLeave={() => {
              if (longPressTimer) {
                clearTimeout(longPressTimer);
                setLongPressTimer(null);
              }
              setIsLongPress(false);
            }}
            onTouchStart={(e) => {
              if (key.digit === "0") {
                const timer = setTimeout(() => {
                  setIsLongPress(true);
                  handleLongPressZero();
                }, 500);
                setLongPressTimer(timer);
              }
            }}
            onTouchEnd={() => {
              if (longPressTimer) {
                clearTimeout(longPressTimer);
                setLongPressTimer(null);
              }
              setTimeout(() => setIsLongPress(false), 100);
            }}
          >
            <span className="text-2xl font-semibold dark:text-white">{key.digit}</span>
            {key.letters && <span className="text-xs text-muted-foreground mt-0.5">{key.letters}</span>}
          </Button>
        ))}
      </div>

      {!inCallMode && (
        <div className="flex gap-3 w-full mt-4">
          <Button variant="outline" size="lg" className="h-14 w-14 shrink-0" onClick={handleBackspace} disabled={!value}>
            <Delete className="h-5 w-5" />
          </Button>
          <Button
            size="lg"
            className="flex-1 h-14 bg-green-600 hover:bg-green-700 text-white"
            onClick={onCall}
            disabled={disabled}
          >
            <Phone className="h-5 w-5 mr-2" />
            Call
          </Button>
        </div>
      )}

      {inCallMode && (
        <div className="flex justify-center w-full mt-4">
          <Button variant="outline" size="lg" className="h-14 w-14" onClick={handleBackspace} disabled={!value}>
            <Delete className="h-5 w-5" />
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center mt-2">Long press 0 for +</p>
    </div>
  );
};

export default DialerKeypad;
